import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { usePrefersReducedMotion } from "../lib/useReducedMotion";
import { IMAGES } from "../lib/images";

const CARD_IMAGES = [IMAGES.servicePpf, IMAGES.serviceTint, IMAGES.serviceWrap];

function TiltCard({ item, image, detail, index }) {
  const reduced = usePrefersReducedMotion();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 20 });

  const onMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={reduced ? {} : { rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
      data-cursor
      data-cursor-label="DETAY"
      data-testid={`service-card-${index}`}
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0F1115]"
    >
      <div className="relative h-56 overflow-hidden">
        <img
          src={image}
          alt={item.name}
          loading="lazy"
          className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-[#0F1115]/40 to-transparent" />
        <span className="heading absolute left-6 top-5 text-6xl text-white/10">0{index + 1}</span>
      </div>
      <div className="p-7">
        <h3 className="heading text-2xl text-white">{item.name}</h3>
        <p className="mt-3 font-inter text-sm text-hars-muted">{item.desc}</p>
        <ul className="mt-5 space-y-2">
          {item.points.map((p, i) => (
            <li key={i} className="flex items-center gap-2 font-inter text-sm text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-hars-orange" />
              {p}
            </li>
          ))}
        </ul>
        <a href="#contact" className="mt-6 inline-flex items-center gap-1 font-inter text-sm font-semibold text-hars-orange">
          {detail} <ArrowUpRight size={16} />
        </a>
      </div>
      <span className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-transparent transition-all duration-500 group-hover:ring-hars-orange/40" />
    </motion.div>
  );
}

export function Services() {
  const { t } = useLanguage();
  return (
    <section id="services" className="relative bg-[#08090B] px-6 py-24 md:px-12 md:py-40" data-testid="services-section">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-14 max-w-2xl">
          <FadeUp>
            <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.services.kicker}</p>
          </FadeUp>
          <SplitReveal text={t.services.title} className="heading text-5xl text-white md:text-7xl" />
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {t.services.items.map((item, i) => (
            <FadeUp key={i} delay={i * 0.1}>
              <TiltCard item={item} image={CARD_IMAGES[i]} detail={t.services.detail} index={i} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
